<template>
  <router-view :style="styleLoad ? '' : 'visibility: hidden'" />
  <div v-if="!styleLoad" class="row items-center justify-center fixed-full">
    <div class="col-auto">
      <q-spinner color="primary" size="xl" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  defineAsyncComponent,
  nextTick,
  onBeforeMount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useQuasar, useMeta, LocalStorage } from 'quasar'

import { useUserStore } from 'stores/receiver/account/user/user'
import { useErrorStore } from 'stores/common/error'
import { useFeatureFlagStore } from 'stores/common/featureFlag'
import { useEventsStore } from 'stores/common/events'
import { useBulkImportStore } from 'src/stores/receiver/bulkImport/bulkImport'

import { useAccessTokenCreation } from 'composables/receiver/auth/accessTokenCreation'
import { useChangeLocale } from 'composables/common/i18n/useChangeLocale'
import { useCheckRoles } from 'composables/receiver/user/useCheckRoles'
import { usePrefetchRedirect } from 'composables/receiver/auth/prefetchRedirect'
import { useUrchinTracking } from 'composables/common/auth/useUrchinTracking'
import { useDuplicateTemplate } from 'composables/receiver/template/edit/useDuplicateTemplate'
import { type LocalesCode, isSupportedLocale } from 'src/i18n/locales'
// INTERCOM
import { useIntercom } from 'composables/common/intercom/useIntercom'
import { useReceiverIntercom } from 'composables/receiver/intercom/useReceiverIntercom'
// END INTERCOM
// SMARTLOOK
import Smartlook from 'smartlook-client'
// END SMARTLOOK

// Favicons
import favicon from 'assets/favicon/favicon.ico'
import favicon16 from 'assets/favicon/icons/favicon-16x16.png'
import favicon32 from 'assets/favicon/icons/favicon-32x32.png'
import favicon192 from 'assets/favicon/icons/android-chrome-192x192.png'
import favicon512 from 'assets/favicon/icons/android-chrome-512x512.png'
import appleTouchIcon from 'assets/favicon/icons/apple-touch-icon.png'

import type { User } from 'types/receiver/account/user'
import type { MetaOptions } from 'quasar/dist/types/meta.js'

const userStore = useUserStore()
const { changeLocale } = useChangeLocale()
const { t } = useI18n()
const $q = useQuasar()
const route = useRoute()
const router = useRouter()

// PREFETCH
defineOptions({
  async preFetch({ store, ssrContext, redirect, currentRoute }) {
    await usePrefetchRedirect(currentRoute, store, redirect, ssrContext)
  },
})

const { accessTokenOnBeforeMount, createAccessToken } = useAccessTokenCreation()
accessTokenOnBeforeMount((LocalStorage.getItem('redirect') as string) || '/')

watch(
  () => route.query.authentication_code,
  async (val) => {
    if (val) {
      await createAccessToken(val as string)
    }
  }
)

// Error notification
onMounted(async () => {
  if (route.query.error) {
    $q.notify({
      color: 'negative',
      message: route.query.error as string,
    })

    await nextTick(() => {
      const queries = { ...route.query }
      delete queries.error
      void router.replace({ query: queries })
    })
  }
})

// MOBILE WARNING
onMounted(() => {
  if ($q.platform.is.mobile) {
    $q.dialog({
      component: defineAsyncComponent(
        () => import('components/common/ui/dialog/MobileWarning.vue')
      ),
    })
  }
})
// Trial success
onMounted(async () => {
  if (route.query.trial) {
    $q.notify({
      color: 'positive',
      message: t('receiver.shop.checkout.success.trial.title'),
    })
    await nextTick(() => {
      const queries = { ...route.query }
      delete queries.trial
      void router.replace({ query: queries })
    })
  }
})

// EVENTS
const { eventSubscribe, watchMeEvent } = useReceiverEvents()
eventSubscribe()
watchMeEvent()

// Watcher for all the async bulk imports (customers, columns, columns constraints and value lists)
const importStore = useBulkImportStore()
importStore.initImportWatcher()

// NAVIGATION GUARD
router.beforeEach((to, from, next) => {
  const user = useUserStore()
  const { isActive } = useFeatureFlagStore()

  // Reset errors from BE
  useErrorStore().backErrors = {}
  // Reset Call to action
  useErrorStore().callToAction = {}

  // Check if user is logged in
  if (to.meta.requiresAuth && !user.isLoggedIn) {
    next({ path: '/login' })
    // Check if user is admin
  } else if (to.meta.requiresAdmin && !user.isAdmin) {
    next(from)
    // Check if user has Roles to access the page
  } else if (
    (to.meta.roles && !useCheckRoles().checkRoles(to.meta.roles)) ||
    (to.meta.oneOfRoles &&
      !useCheckRoles().checkRoles(to.meta.oneOfRoles, false))
  ) {
    next(from)
    $q.notify({
      color: 'negative',
      message: t('error.auth.roles'),
    })
    // Check if user try to access login page while being logged in
  } else if (to.meta.flag && !isActive(to.meta.flag)) {
    $q.notify({
      color: 'warning',
      message: t('error.auth.featureFlag'),
    })
    next(from)
  } else if (to.path === '/login' && user.isLoggedIn) {
    next({ name: 'overview' })
  } else {
    next()
  }
})

const styleLoad = ref(false)

onMounted(async () => {
  // URCHIN TRACKING
  const { saveUrchin } = useUrchinTracking()
  await saveUrchin()

  // Change locale
  let langIso: LocalesCode = 'en'
  // If user is logged in get his locale
  if (userStore.user) {
    langIso = userStore.user.preferences.language
  } else {
    // Try to get the browser locale
    const browserLocale = $q.lang.getLocale()
    // Set default: browser locale if supported or fallback to en
    langIso =
      browserLocale && isSupportedLocale(browserLocale) ? browserLocale : 'en'
  }
  await changeLocale(langIso)

  styleLoad.value = true

  // INTERCOM
  if (
    process.env.PROD &&
    process.env.INFRA_ENV !== 'draft' &&
    process.env.INFRA_ENV !== 'staging'
  ) {
    // Boot Intercom
    window.Intercom('boot', {
      app_id: process.env.INTERCOM_APP_ID,
    })

    const { intercomBoot, intercomShutdown, intercomUpdate } = useIntercom()
    const { getUserData } = useReceiverIntercom()

    // Update intercom
    watch(
      () => userStore.user,
      (val) => {
        if (val) {
          intercomUpdate(getUserData())
        } else {
          intercomShutdown()
          intercomBoot()
        }
      },
      { immediate: true }
    )
  }

  // SMARTLOOK
  if (
    process.env.PROD &&
    !userStore.user?.is_admin &&
    !userStore.user?.impersonator
  ) {
    Smartlook.init(process.env.SMARTLOOK_KEY as string, { region: 'eu' })
    watch(
      () => userStore.user,
      (val, oldVal) => {
        if (val?.is_admin || val?.impersonator) {
          Smartlook.disable()
        } else if (val && val.id !== oldVal?.id) {
          Smartlook.identify(val.id, {
            email: val.email,
            language: val.preferences.language,
          })
        }
      },
      { immediate: true }
    )
  }
  // END SMARTLOOK

  // GTAG
  if (process.env.PROD) {
    const getInfo = (user: User | null) => ({
      user_id: user?.id,
      job_title: user?.profile.job,
    })

    // Push config variables
    window.dataLayer.push({
      event: 'config',
      ...getInfo(userStore.user),
    })
    // Initialize GTM
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })

    watch(
      () => userStore.user,
      (val, oldVal) => {
        if (
          val &&
          (val.id !== oldVal?.id || val.profile.job !== oldVal?.profile.job)
        ) {
          // Update GTM variables
          window.dataLayer.push({
            event: 'set',
            ...getInfo(val),
          })
        }
      }
    )
  }
  // END GTAG
})

// TGGL
if (process.env.PROD) {
  const featureFlagStore = useFeatureFlagStore()
  featureFlagStore.init()

  onMounted(() => {
    featureFlagStore.updateWatcher(() => userStore.user?.feature_flag_context)
  })
}
// END TGGL

// TEMPLATE DUPLICATION
const { notifyEndOfDuplication } = useDuplicateTemplate()
notifyEndOfDuplication()

// META
useMeta(() => {
  const metas = {
    link: {
      appleTouch: {
        rel: 'apple-touch-icon',
        href: appleTouchIcon,
      },
      favicon512: {
        rel: 'icon',
        type: 'image/png',
        sizes: '512x512',
        href: favicon512,
      },
      favicon192: {
        rel: 'icon',
        type: 'image/png',
        sizes: '192x192',
        href: favicon192,
      },
      favicon32: {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: favicon32,
      },
      favicon16: {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: favicon16,
      },
      favicon: {
        rel: 'icon',
        type: 'image/ico',
        href: favicon,
      },
    },
  } as MetaOptions

  // COOKIEYES
  if (process.env.PROD) {
    metas.script = {
      cookieyes: {
        type: 'text/javascript',
        async: 'true',
        src: `https://cdn-cookieyes.com/client_data/${process.env.COOKIEYES_KEY}/script.js`,
      },
    }
  }

  return metas
})

function useReceiverEvents() {
  const eventsStore = useEventsStore()

  const eventSubscribe = () => {
    onBeforeMount(() => {
      // Subscribe to EventSource
      if (userStore.isLoggedIn && userStore.user) {
        eventsStore.subscribeEvents([userStore.user.channel_name])
      }
    })

    onMounted(() => {
      window.addEventListener('beforeunload', () => {
        eventsStore.close()
      })
    })
  }

  const watchMeEvent = () => {
    watch(
      () => eventsStore.eventData,
      async (val) => {
        if (val?.type === 'me') {
          await userStore.fetchUserInfo()
        }
      }
    )
  }

  return { eventSubscribe, watchMeEvent }
}
</script>
